/**
 * Environment Configuration
 * Centralized environment and app configuration
 */

export interface AppConfig {
  // Environment
  env: 'development' | 'staging' | 'production';
  debug: boolean;

  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
    retries: number;
  };

  // Security Configuration
  security: {
    enableAuditLogs: boolean;
    enableRateLimiting: boolean;
    enableMFA: boolean;
    sessionTimeout: number; // in minutes
    tokenRefreshBuffer: number; // in seconds
    maxFailedLoginAttempts: number;
    failedLoginWindow: number; // in minutes
  };

  // Feature Flags
  features: {
    enableModuleSystem: boolean;
    enableFeatureFlags: boolean;
    enableAnalytics: boolean;
    enableNotifications: boolean;
  };

  // Logging Configuration
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableConsole: boolean;
    enableRemote: boolean;
    enablePerformance: boolean;
  };

  // Performance Configuration
  performance: {
    enableLazyLoading: boolean;
    enableCodeSplitting: boolean;
    chunkSizeWarningLimit: number; // in KB
  };

  // UI Configuration
  ui: {
    theme: 'light' | 'dark' | 'auto';
    language: string;
    dateFormat: string;
    timeFormat: string;
    currency: string;
    timezone: string;
  };
}

// Default configuration
const defaultConfig: AppConfig = {
  env: (process.env.NODE_ENV as any) || 'development',
  debug: process.env.NODE_ENV === 'development',

  api: {
    // Prefer runtime origin so a single pooled web build serves multiple
    // tenants correctly. Falls back to build-time REACT_APP_API_URL for
    // node/SSR/test contexts where window is undefined, then to localhost
    // for local dev. Fix root: pooled kodia-web served three subdomains
    // but hardcoded kodia.basiq360.tech as its API base, so Carnival's
    // browser sent login to Kodia backend which resolved org 7 via Origin
    // header and returned Kodia's branding + rejected carnival_admin creds.
    baseUrl:
      (typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}/api`
        : undefined) ||
      process.env.REACT_APP_API_URL ||
      // Relative, never a hardcoded host. A literal localhost here is exactly
      // what shipped to prod on 2026-06-22 and took logins down for 4 hours —
      // the bundle told every user's browser to call their own machine.
      // '/api' is correct for every tenant (nginx routes /api/* per domain)
      // and cannot point off-origin no matter how the image was built.
      '/api',
    timeout: 30000, // 30 seconds
    retries: 3,
  },

  security: {
    enableAuditLogs: true,
    enableRateLimiting: true,
    enableMFA: process.env.REACT_APP_ENABLE_MFA === 'true',
    sessionTimeout: 30, // 30 minutes
    tokenRefreshBuffer: 300, // 5 minutes
    maxFailedLoginAttempts: 5,
    failedLoginWindow: 15, // 15 minutes
  },

  features: {
    enableModuleSystem: true,
    enableFeatureFlags: true,
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableNotifications: true,
  },

  logging: {
    level: process.env.NODE_ENV === 'production' ? 'warn' : 'debug',
    enableConsole: process.env.NODE_ENV !== 'production',
    enableRemote: process.env.NODE_ENV === 'production',
    enablePerformance: process.env.REACT_APP_ENABLE_PERFORMANCE === 'true',
  },

  performance: {
    enableLazyLoading: true,
    enableCodeSplitting: true,
    chunkSizeWarningLimit: 500, // 500 KB
  },

  ui: {
    theme: 'auto',
    language: 'en',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    currency: 'USD',
    timezone: 'UTC',
  },
};

// Get configuration
export const getConfig = (): AppConfig => {
  return defaultConfig;
};

// Get specific config value
export const getConfigValue = <K extends keyof AppConfig>(key: K): AppConfig[K] => {
  return defaultConfig[key];
};

// Check if development environment
export const isDevelopment = (): boolean => {
  return defaultConfig.env === 'development';
};

// Check if production environment
export const isProduction = (): boolean => {
  return defaultConfig.env === 'production';
};

// Check if staging environment
export const isStaging = (): boolean => {
  return defaultConfig.env === 'staging';
};

// Export default config
export default defaultConfig;
